import { useCallback, useEffect, useState } from 'react';

import { broadcastTradingSession } from '@/features/tradingSession/broadcast';
import {
    anchorLabel,
    useAnchorChain,
    useWallet,
    useWalletNetwork,
} from '@/features/wallet';
import { describeErrorText } from '@/lib/errors';
import { shortenAddress } from '@/lib/utils';

import { isOAuthAccountHashInUseError } from '../accountBinding';
import { useOAuthBindingDialogStore } from '../oauthBindingDialogStore';
import {
    completeOAuthAccountBindingWithWallet,
    getOwnedAuthorizationWallet,
} from '../walletAccountBinding';

import { useOAuthSession } from './useOAuthSession';

export interface LinkOAuthAccount {
    /**
     * Adds the pending login to the Snappy account the connected wallet already
     * owns (one transaction on the anchor), then rebinds the session to it.
     * Resolves true once the login is bound; false leaves the reason in `error`.
     */
    link: () => Promise<boolean>;
    isWorking: boolean;
    error: string | null;
    /** The authorization wallet the login joins; null until read. */
    accountWallet: string | null;
}

/**
 * The "existing wallet" route of the binding dialog, as a hook, so onboarding
 * can run the same linking inline instead of over a second dialog.
 */
export function useLinkOAuthAccount(
    accountHash: string | null,
): LinkOAuthAccount {
    const { address } = useWallet();
    const { ensureAnchor } = useWalletNetwork();
    const { anchorChainId } = useAnchorChain();
    const { rebind } = useOAuthSession();
    const [isWorking, setIsWorking] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [accountWallet, setAccountWallet] = useState<string | null>(null);

    useEffect(() => {
        setAccountWallet(null);
        if (!address) return;
        let cancelled = false;
        getOwnedAuthorizationWallet(address, anchorChainId)
            .then((wallet) => {
                if (!cancelled) setAccountWallet(wallet);
            })
            .catch(() => {
                // display only; the link itself re-reads the chain
            });
        return () => {
            cancelled = true;
        };
    }, [address, anchorChainId]);

    const link = useCallback(async (): Promise<boolean> => {
        setError(null);
        if (!accountHash || !address) {
            setError('Connect your wallet to continue.');
            return false;
        }
        setIsWorking(true);
        try {
            if (!(await ensureAnchor())) {
                throw new Error(
                    `Switch your connected wallet to ${anchorLabel(anchorChainId)}.`,
                );
            }
            await completeOAuthAccountBindingWithWallet(
                address,
                accountHash,
                'existing-wallet',
                anchorChainId,
            );
            const rebound = await rebind();
            if (rebound.bindingState !== 'bound') {
                throw new Error(
                    'The account was updated, but the login is not visible on it yet. Please try again.',
                );
            }
            useOAuthBindingDialogStore.getState().complete();
            broadcastTradingSession({ type: 'changed' });
            return true;
        } catch (caught) {
            if (isOAuthAccountHashInUseError(caught)) {
                setError(
                    `This login is already linked to a Snappy account owned by another wallet (${shortenAddress(caught.registeredOwner)}).`,
                );
                return false;
            }
            setError(
                describeErrorText(caught, { operation: 'Account linking' }),
            );
            return false;
        } finally {
            setIsWorking(false);
        }
    }, [accountHash, address, anchorChainId, ensureAnchor, rebind]);

    return { link, isWorking, error, accountWallet };
}
